/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  Phone,
  MessageCircle,
  ExternalLink,
  Copy,
  Check,
  User,
  Building,
  Receipt,
  Package,
  Truck,
  ShieldAlert,
  HelpCircle,
  Search,
  X,
  QrCode,
  Send,
  Sparkles,
  MapPin,
  Clock,
  Briefcase,
  CheckCircle2,
  FileText,
  AlertTriangle,
  History,
  Trash2,
  Building2
} from 'lucide-react';
import { Client, CostCenter, Folio, PreInvoice, SupportArea } from '../types.ts';

interface ClientChatModuleProps {
  client: Client;
  costCenter: CostCenter | null;
  folios: Folio[];
  preInvoices: PreInvoice[];
  currentUser?: { name: string; email?: string; role?: string; clientCode?: string } | null;
}

interface SentWhatsAppLog {
  id: string;
  timestamp: string;
  departmentName: string;
  agentName: string;
  solicitanteName: string;
  folioId?: string;
  topicTitle: string;
  messageText: string;
}

export default function ClientChatModule({ 
  client, 
  costCenter, 
  folios, 
  preInvoices,
  currentUser 
}: ClientChatModuleProps) {

  // Load all configured departments dynamically or use robust default areas
  const allDepartmentsList = useMemo<SupportArea[]>(() => {
    const savedList = localStorage.getItem('snfsic_whatsapp_support_areas');
    if (savedList) {
      try {
        const parsed = JSON.parse(savedList);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (e) {
        console.error("Error parsing saved support areas", e);
      }
    }

    // Fallback default support areas
    return [
      {
        id: 'facturacion',
        name: 'Departamento de Facturación y Cobranza',
        agent: 'Lic. Ana Karen Silva',
        phone: '8712397902',
        desc: 'Revisiones de prefactura, complementos de pago, refacturación y estado de cuenta.',
        color: 'emerald',
        allClients: true,
        clientIds: [],
        allCostCenters: true,
        costCenterIds: []
      },
      {
        id: 'almacen',
        name: 'Control de Almacén y Logística',
        agent: 'Ing. Mateo Rodríguez',
        phone: '4622996955',
        desc: 'Recepción de material, conteo de piezas, despacho y granallado.',
        color: 'blue',
        allClients: true,
        clientIds: [],
        allCostCenters: true,
        costCenterIds: []
      },
      {
        id: 'calidad',
        name: 'Aseguramiento de Calidad y Retrabajos',
        agent: 'Ing. Roberto Garza',
        phone: '8717894512',
        desc: 'Inspecciones físicas, disconformidades, libramientos y reportes de retrabajo.',
        color: 'amber',
        allClients: true,
        clientIds: [],
        allCostCenters: true,
        costCenterIds: []
      },
      {
        id: 'choferes',
        name: 'Atención a Choferes y Unidades en Ruta',
        agent: 'Lic. Carlos Fuentes',
        phone: '8711122334',
        desc: 'Monitoreo de choferes, ubicación GPS en tiempo real y asistencia en trayecto.',
        color: 'indigo',
        allClients: true,
        clientIds: [],
        allCostCenters: true,
        costCenterIds: []
      }
    ];
  }, []);

  // Filter departments by active Client AND active Cost Center:
  // If a client is active, ONLY show lines that explicitly include this client OR have global coverage (allClients = true)
  // If a cost center is active, ONLY show lines that explicitly include this cost center OR have global coverage (allCostCenters = true)
  const departmentsList = useMemo<SupportArea[]>(() => {
    const normClientId = client?.id?.toLowerCase().trim();
    const normClientCode = client?.code?.toLowerCase().trim();
    const normClientName = client?.name?.toLowerCase().trim();

    const normCostCenterId = costCenter?.id?.toLowerCase().trim();
    const normCostCenterCode = costCenter?.code?.toLowerCase().trim();
    const normCostCenterName = costCenter?.name?.toLowerCase().trim();

    const filtered = allDepartmentsList.filter(dept => {
      // 1. Client Matching
      let clientMatches = false;
      if (dept.allClients === true) {
        clientMatches = true;
      } else if (Array.isArray(dept.clientIds) && dept.clientIds.length > 0) {
        clientMatches = dept.clientIds.some(rawId => {
          const normId = rawId?.toLowerCase().trim();
          return (
            normId === normClientId ||
            normId === normClientCode ||
            normId === normClientName
          );
        });
      } else if (dept.clientId) {
        const normId = dept.clientId.toLowerCase().trim();
        clientMatches = (
          normId === normClientId ||
          normId === normClientCode ||
          normId === normClientName
        );
      } else if (dept.allClients === undefined && (!dept.clientIds || dept.clientIds.length === 0) && !dept.clientId) {
        clientMatches = true;
      }

      if (!clientMatches) return false;

      // 2. Cost Center Matching (if cost center is active)
      if (costCenter) {
        let ccMatches = false;
        if (dept.allCostCenters === true) {
          ccMatches = true;
        } else if (Array.isArray(dept.costCenterIds) && dept.costCenterIds.length > 0) {
          ccMatches = dept.costCenterIds.some(rawId => {
            const normId = rawId?.toLowerCase().trim();
            return (
              normId === normCostCenterId ||
              normId === normCostCenterCode ||
              normId === normCostCenterName ||
              (normCostCenterCode && normId === normCostCenterCode.replace('cc-', ''))
            );
          });
        } else if (dept.allCostCenters === undefined && (!dept.costCenterIds || dept.costCenterIds.length === 0)) {
          ccMatches = true;
        }

        if (!ccMatches) return false;
      }

      return true;
    });

    return filtered;
  }, [allDepartmentsList, client, costCenter]);

  // Format phone for clean display
  const formatPhoneDisplay = (num: string) => {
    const clean = num.replace(/\D/g, '');
    if (clean.length === 10) {
      return `+52 (${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    return num;
  };

  // Department Roles Mapping (Paso 2: Roles correspondientes al departamento)
  const DEPARTMENT_ROLES: Record<string, string[]> = {
    facturacion: [
      'Administración / Cuentas por Pagar',
      'Contabilidad y Finanzas',
      'Coordinador de Compras',
      'Gerente General / Representante',
      'Socio Comercial'
    ],
    almacen: [
      'Coordinador de Logística',
      'Jefe de Almacén / Recibo',
      'Supervisor de Embarques',
      'Encargado de Patio / Carga',
      'Socio Comercial'
    ],
    calidad: [
      'Aseguramiento de Calidad',
      'Inspector de Calidad',
      'Ingeniero de Procesos',
      'Auditor de Calidad / Planta',
      'Socio Comercial'
    ],
    choferes: [
      'Chofer / Operador en Ruta',
      'Coordinador de Tráfico',
      'Monitoreo y Logística',
      'Auxiliar de Transporte',
      'Socio Comercial'
    ]
  };

  const DEFAULT_DEPARTMENT_ROLES = [
    'Coordinador de Operaciones',
    'Representante Autorizado',
    'Gerente / Supervisor',
    'Socio Comercial'
  ];

  // Department Topics Mapping (Paso 3: Motivos correspondientes al departamento)
  interface TopicOption {
    key: string;
    title: string;
    icon: React.ElementType;
    color: string;
    defaultText: string;
  }

  const DEPARTMENT_TOPICS: Record<string, TopicOption[]> = {
    facturacion: [
      {
        key: 'FACTURACION',
        title: 'Solicitud / Aclaración de Factura',
        icon: Receipt,
        color: 'indigo',
        defaultText: 'Estimado equipo de Facturación: solicito apoyo con la revisión, emisión o desglose de comprobante fiscal.'
      },
      {
        key: 'COMPLEMENTO_PAGO',
        title: 'Complemento de Pago / Estado de Cuenta',
        icon: FileText,
        color: 'emerald',
        defaultText: 'Buen día, solicito el envío o validación de complementos de pago pendientes y el estado de cuenta actualizado.'
      },
      {
        key: 'REFACTURACION',
        title: 'Refacturación / Corrección CFDI',
        icon: Receipt,
        color: 'blue',
        defaultText: 'Solicito apoyo con la refacturación o cancelación/sustitución por corrección en datos fiscales o importes.'
      },
      {
        key: 'PERSONALIZADO',
        title: 'Mensaje Directo Personalizado',
        icon: MessageCircle,
        color: 'slate',
        defaultText: ''
      }
    ],
    almacen: [
      {
        key: 'RECEPCION_MATERIAL',
        title: 'Recepción y Conteo en Almacén',
        icon: Package,
        color: 'emerald',
        defaultText: 'Hola, solicito confirmar la recepción física del lote en almacén y la validación de piezas registradas.'
      },
      {
        key: 'ESTATUS_ENTREGA',
        title: 'Estatus de Entrega / Despacho',
        icon: Truck,
        color: 'blue',
        defaultText: 'Buen día, me pongo en contacto para consultar el avance actual, despacho de piezas y estatus de carga.'
      },
      {
        key: 'GRANALLADO_PROCESO',
        title: 'Validación de Granallado / Limpieza',
        icon: Sparkles,
        color: 'amber',
        defaultText: 'Solicito estatus del proceso de granallado y preparación de superficie para el lote en planta.'
      },
      {
        key: 'PERSONALIZADO',
        title: 'Mensaje Directo Personalizado',
        icon: MessageCircle,
        color: 'slate',
        defaultText: ''
      }
    ],
    calidad: [
      {
        key: 'RETRABAJO_INCIDENCIA',
        title: 'Reporte de Incidencia o Devolución',
        icon: ShieldAlert,
        color: 'rose',
        defaultText: 'Atención equipo de Calidad: notifico una novedad o disconformidad en las piezas recibidas para evaluación y seguimiento prioritario.'
      },
      {
        key: 'INSPECCION_LIBERACION',
        title: 'Solicitud de Inspección / Liberación',
        icon: CheckCircle2,
        color: 'emerald',
        defaultText: 'Buen día, solicito reporte de inspección física dimensional/adherencia y certificado de liberación de calidad.'
      },
      {
        key: 'REPORTE_NO_CONFORMIDAD',
        title: 'Reporte de No Conformidad / Scrap',
        icon: AlertTriangle,
        color: 'amber',
        defaultText: 'Notificación formal de piezas no conformes detectadas en línea para análisis de causa raíz y acción correctiva.'
      },
      {
        key: 'PERSONALIZADO',
        title: 'Mensaje Directo Personalizado',
        icon: MessageCircle,
        color: 'slate',
        defaultText: ''
      }
    ],
    choferes: [
      {
        key: 'RUTA_CHOFER',
        title: 'Atención y Ubicación Chofer en Ruta',
        icon: MapPin,
        color: 'amber',
        defaultText: 'Buen día, solicitamos coordinación con el operador en ruta y compartición de ubicación geolocalizada en vivo.'
      },
      {
        key: 'LLEGADA_PLANTA',
        title: 'Aviso de Arribo / Descarga en Planta',
        icon: Truck,
        color: 'indigo',
        defaultText: 'Informo arribo de unidad a patio / caseta para asignación de rampa de descarga o carga de material.'
      },
      {
        key: 'INCIDENCIA_TRAYECTO',
        title: 'Incidencia en Tránsito / Retraso',
        icon: AlertTriangle,
        color: 'rose',
        defaultText: 'Notifico contratiempo en ruta (tráfico, retén, falla mecánica o clima) que modificará la hora estimada de llegada.'
      },
      {
        key: 'PERSONALIZADO',
        title: 'Mensaje Directo Personalizado',
        icon: MessageCircle,
        color: 'slate',
        defaultText: ''
      }
    ]
  };

  // States
  const [activeDeptId, setActiveDeptId] = useState<string>(() => departmentsList[0]?.id || 'facturacion');
  
  // Solicitante (Applicant) Details
  const [solicitanteName, setSolicitanteName] = useState<string>(() => {
    return currentUser?.name || client.name || '';
  });
  const [solicitanteRole, setSolicitanteRole] = useState<string>(() => {
    const initialDept = departmentsList[0]?.id || 'facturacion';
    return DEPARTMENT_ROLES[initialDept]?.[0] || 'Administración / Cuentas por Pagar';
  });
  const [solicitantePhone, setSolicitantePhone] = useState<string>('');
  
  // Topic / Motivo
  const [selectedTopicKey, setSelectedTopicKey] = useState<string>(() => {
    const initialDept = departmentsList[0]?.id || 'facturacion';
    return DEPARTMENT_TOPICS[initialDept]?.[0]?.key || 'FACTURACION';
  });
  const [customDetailText, setCustomDetailText] = useState<string>('');
  
  // Folio Selection
  const [selectedFolioId, setSelectedFolioId] = useState<string>('');
  const [searchFolioQuery, setSearchFolioQuery] = useState<string>('');

  // UI States
  const [copiedSuccess, setCopiedSuccess] = useState<boolean>(false);
  const [showQrModal, setShowQrModal] = useState<boolean>(false);
  
  // Local Dispatch Log
  const [dispatchLogs, setDispatchLogs] = useState<SentWhatsAppLog[]>(() => {
    const saved = localStorage.getItem(`snfsic_wa_dispatches_${client.id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error(e);
      }
    }
    return [];
  });

  // Save logs to localStorage
  useEffect(() => {
    localStorage.setItem(`snfsic_wa_dispatches_${client.id}`, JSON.stringify(dispatchLogs));
  }, [dispatchLogs, client.id]);

  // Ensure activeDeptId stays valid if departmentsList changes due to costCenter change
  useEffect(() => {
    if (departmentsList.length > 0 && !departmentsList.some(d => d.id === activeDeptId)) {
      setActiveDeptId(departmentsList[0].id);
    }
  }, [departmentsList, activeDeptId]);

  // Active department object (safe fallback if no departments match current cost center)
  const activeDept = useMemo<SupportArea | undefined>(() => {
    if (departmentsList.length === 0) return undefined;
    return departmentsList.find(d => d.id === activeDeptId) || departmentsList[0];
  }, [departmentsList, activeDeptId]);

  // Dynamic available roles based on selected department
  const availableRoles = useMemo(() => {
    if (!activeDeptId) return DEFAULT_DEPARTMENT_ROLES;
    return DEPARTMENT_ROLES[activeDeptId] || DEFAULT_DEPARTMENT_ROLES;
  }, [activeDeptId]);

  // Dynamic available topics based on selected department
  const availableTopics = useMemo(() => {
    if (!activeDeptId) {
      return [
        {
          key: 'GENERAL_CONSULTA',
          title: 'Consulta General',
          icon: HelpCircle,
          color: 'blue',
          defaultText: 'Buen día, solicito atención y seguimiento para el siguiente asunto.'
        }
      ];
    }
    return DEPARTMENT_TOPICS[activeDeptId] || [
      {
        key: 'GENERAL_CONSULTA',
        title: 'Consulta General',
        icon: HelpCircle,
        color: 'blue',
        defaultText: 'Buen día, solicito atención y seguimiento para el siguiente asunto.'
      },
      {
        key: 'PERSONALIZADO',
        title: 'Mensaje Directo Personalizado',
        icon: MessageCircle,
        color: 'slate',
        defaultText: ''
      }
    ];
  }, [activeDeptId]);

  // Department selection handler that auto-adjusts role and topic
  const handleSelectDepartment = (deptId: string) => {
    setActiveDeptId(deptId);
    
    // Auto-adjust solicitante role if not in new department's allowed roles
    const nextRoles = DEPARTMENT_ROLES[deptId] || DEFAULT_DEPARTMENT_ROLES;
    if (!nextRoles.includes(solicitanteRole)) {
      setSolicitanteRole(nextRoles[0]);
    }

    // Auto-adjust topic if current topic is not in new department's topics
    const nextTopics = DEPARTMENT_TOPICS[deptId] || [];
    if (!nextTopics.some(t => t.key === selectedTopicKey)) {
      setSelectedTopicKey(nextTopics[0]?.key || 'PERSONALIZADO');
    }
  };

  // Client folios list
  const clientFolios = useMemo(() => {
    return folios.filter(f => f.clientId === client.id || f.clientName === client.name);
  }, [folios, client.id, client.name]);

  // Filtered folios for dropdown picker
  const filteredFoliosList = useMemo(() => {
    if (!searchFolioQuery) return clientFolios;
    const q = searchFolioQuery.toLowerCase();
    return clientFolios.filter(f => 
      f.id.toLowerCase().includes(q) ||
      (f.costCenterName && f.costCenterName.toLowerCase().includes(q)) ||
      (f.status && f.status.toLowerCase().includes(q))
    );
  }, [clientFolios, searchFolioQuery]);

  // Selected folio object
  const currentSelectedFolio = useMemo(() => {
    return clientFolios.find(f => f.id === selectedFolioId);
  }, [clientFolios, selectedFolioId]);

  const currentTopic = useMemo(() => {
    return availableTopics.find(t => t.key === selectedTopicKey) || availableTopics[0];
  }, [availableTopics, selectedTopicKey]);

  // Construct formatted WhatsApp message body
  const formattedWhatsAppMessage = useMemo(() => {
    let msg = `*SISTEMA DE INFORMACIÓN DE CALIDAD (SIC)*\n`;
    msg += `*NOTIFICACIÓN DIRECTA DE ATENCIÓN VIA WHATSAPP*\n\n`;

    // Solicitante / Notificador identification
    const finalSolicitante = solicitanteName.trim() || currentUser?.name || client.name || 'Representante Autorizado';
    msg += `👤 *SOLICITANTE:* ${finalSolicitante}\n`;
    msg += `🏢 *EMPRESA / CLIENTE:* ${client.name} (${client.code || 'SOCIO'})\n`;
    if (solicitanteRole) {
      msg += `💼 *CARGO / ROL:* ${solicitanteRole}\n`;
    }
    if (costCenter) {
      msg += `📍 *PLANTA / C. COSTO:* ${costCenter.name} (${costCenter.code})\n`;
    }
    if (solicitantePhone.trim()) {
      msg += `📱 *TELÉFONO CONTACTO:* ${solicitantePhone.trim()}\n`;
    }
    msg += `\n`;

    // Department recipient
    msg += `📌 *DEPARTAMENTO DESTINO:* ${activeDept?.name || 'Atención y Soporte'}\n`;
    msg += `👨‍💼 *ATENCIÓN A:* ${activeDept?.agent || 'Encargado de Área'}\n`;
    msg += `🏷️ *MOTIVO DE NOTIFICACIÓN:* ${currentTopic.title}\n\n`;

    // Associated Folio
    if (currentSelectedFolio) {
      msg += `📑 *FOLIO DE REFERENCIA:* ${currentSelectedFolio.id}\n`;
      msg += `   • *Estado Actual:* ${currentSelectedFolio.status}\n`;
      msg += `   • *Planta / Destino:* ${currentSelectedFolio.costCenterName}\n`;
      msg += `   • *Total Piezas:* ${currentSelectedFolio.items.reduce((sum, item) => sum + (item.quantity || 0), 0)} pzas\n`;
      if (currentSelectedFolio.hasGranallado) {
        msg += `   • *Proceso:* Incluye Granallado\n`;
      }
      msg += `\n`;
    }

    // Message details
    msg += `💬 *DETALLE DEL MENSAJE:*\n`;
    if (selectedTopicKey !== 'PERSONALIZADO' && currentTopic.defaultText) {
      msg += `${currentTopic.defaultText}\n`;
    }

    if (customDetailText.trim()) {
      msg += `\n"${customDetailText.trim()}"\n`;
    }

    msg += `\n---\n_Notificación generada directamente desde el Portal Oficial de Operaciones_`;

    return msg;
  }, [
    solicitanteName, 
    currentUser?.name, 
    client, 
    solicitanteRole, 
    costCenter, 
    solicitantePhone, 
    activeDept, 
    currentTopic, 
    currentSelectedFolio, 
    selectedTopicKey, 
    customDetailText
  ]);

  // Construct direct WhatsApp link
  const whatsappUrl = useMemo(() => {
    if (!activeDept?.phone) return '#';
    const cleanPhone = activeDept.phone.replace(/\D/g, '');
    const encoded = encodeURIComponent(formattedWhatsAppMessage);
    return `https://wa.me/52${cleanPhone}?text=${encoded}`;
  }, [activeDept?.phone, formattedWhatsAppMessage]);

  // Copy message to clipboard
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(formattedWhatsAppMessage);
      setCopiedSuccess(true);
      setTimeout(() => setCopiedSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  // Register dispatch in local log and open WhatsApp
  const handleOpenWhatsApp = () => {
    if (!activeDept || !activeDept.phone) return;
    const newLog: SentWhatsAppLog = {
      id: `wa-${Date.now()}`,
      timestamp: new Date().toISOString(),
      departmentName: activeDept.name,
      agentName: activeDept.agent,
      solicitanteName: solicitanteName.trim() || currentUser?.name || client.name,
      folioId: selectedFolioId || undefined,
      topicTitle: currentTopic?.title || 'Atención General',
      messageText: customDetailText.trim() || currentTopic?.defaultText || ''
    };

    setDispatchLogs(prev => [newLog, ...prev.slice(0, 19)]); // Keep last 20 logs
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClearLog = (id: string) => {
    setDispatchLogs(prev => prev.filter(l => l.id !== id));
  };

  return (
    <div className="space-y-6" id="client-whatsapp-direct-module">
      
      {/* HEADER BANNER */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 border border-slate-800 rounded-2xl p-6 shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 text-emerald-400 pointer-events-none">
          <MessageCircle className="h-40 w-40" />
        </div>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 relative z-10">
          <div className="space-y-1.5 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-wider rounded-md font-mono flex items-center gap-1.5">
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span>Atención Directa 24/7</span>
              </span>
              <span className="text-xs text-slate-400 font-bold font-mono uppercase tracking-wider">
                Línea Directa WhatsApp • {client.name}
              </span>
            </div>
            
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight font-display">
              Centro de Notificaciones y Soporte Directo
            </h2>
            <p className="text-xs md:text-sm text-slate-300 leading-relaxed font-medium">
              Envía notificaciones estructuradas directamente al personal operativo de **Facturación**, **Almacén**, **Calidad** y **Choferes** sin intermediarios ni chatbots. Confirma tu nombre como solicitante para agilizar el seguimiento.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0 self-start md:self-auto">
            <div className="bg-slate-800/80 border border-slate-700/80 px-4 py-2.5 rounded-xl text-right">
              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest block font-mono">
                Respuesta Inmediata
              </span>
              <span className="text-xs text-emerald-400 font-extrabold flex items-center justify-end gap-1 mt-0.5 font-sans">
                <Clock className="h-3.5 w-3.5 shrink-0" /> Canal Directo sin Filas
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN INTERACTIVE GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* LEFT COLUMN: Department, Applicant Name Confirmation & Topic Selector */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* STEP 1: DEPARTMENT SELECTOR (SELECCIÓN DE DEPARTAMENTO DESTINO) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-3.5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl font-bold">
                  <Building className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider font-mono text-slate-800 flex items-center gap-2">
                    <span>PASO 1: Seleccionar Departamento Destino</span>
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full font-mono">
                      {departmentsList.length} Áreas
                    </span>
                  </h3>
                  <p className="text-[10.5px] text-slate-500 font-medium">
                    Indica con quién deseas comunicarte (Facturación, Almacén, Calidad o Choferes)
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-mono font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md border border-emerald-100">
                Paso Inicial
              </span>
            </div>

            {/* Client & Cost Center Active Filter Indicator */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="bg-blue-50/70 border border-blue-200/80 rounded-xl p-2.5 flex items-center justify-between gap-2 flex-1">
                <div className="flex items-center gap-2 text-xs text-blue-950 font-medium min-w-0">
                  <Building className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="truncate">
                    Líneas asignadas a: <strong className="font-bold">{client?.name || 'Cliente Actual'} ({client?.code || 'SOCIO'})</strong>
                  </span>
                </div>
                <span className="text-[9px] bg-blue-200/70 text-blue-900 font-bold font-mono px-2 py-0.5 rounded-full shrink-0">
                  Atención por Cliente
                </span>
              </div>

              {costCenter && (
                <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-2.5 flex items-center justify-between gap-2 flex-1">
                  <div className="flex items-center gap-2 text-xs text-emerald-900 font-medium min-w-0">
                    <Building2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span className="truncate">
                      Sede: <strong className="font-bold">{costCenter.name} ({costCenter.code})</strong>
                    </span>
                  </div>
                  <span className="text-[9px] bg-emerald-200/70 text-emerald-900 font-bold font-mono px-2 py-0.5 rounded-full shrink-0">
                    Sede Activa
                  </span>
                </div>
              )}
            </div>

            {departmentsList.length === 0 ? (
              <div className="bg-amber-50/70 border border-amber-200/90 rounded-2xl p-6 text-center space-y-3 my-2">
                <div className="h-12 w-12 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xs">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider font-mono">
                    Sin líneas de soporte asignadas a este cliente / sede
                  </h4>
                  <p className="text-[11px] text-slate-600 max-w-md mx-auto leading-relaxed">
                    Actualmente no hay encargados ni líneas de WhatsApp habilitadas para <strong className="text-slate-800">{client?.name || 'este cliente'}</strong> {costCenter ? `en la sede ${costCenter.name} (${costCenter.code})` : ''}.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {departmentsList.map((area) => {
                  const isSelected = activeDeptId === area.id;
                  let IconComp = HelpCircle;
                  if (area.id === 'facturacion') IconComp = Receipt;
                  else if (area.id === 'almacen') IconComp = Package;
                  else if (area.id === 'calidad') IconComp = ShieldAlert;
                  else if (area.id === 'choferes') IconComp = Truck;

                  return (
                    <button
                      key={area.id}
                      type="button"
                      onClick={() => handleSelectDepartment(area.id)}
                      className={`text-left p-3.5 rounded-2xl border transition-all cursor-pointer relative group flex items-start gap-3 ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50/40 shadow-xs ring-2 ring-emerald-500/20'
                          : 'border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/50'
                      }`}
                    >
                      <div className={`p-2.5 rounded-xl shrink-0 transition-colors ${
                        isSelected
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200/70'
                      }`}>
                        <IconComp className="h-5 w-5" />
                      </div>

                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-slate-900 group-hover:text-emerald-700 transition-colors truncate">
                            {area.name}
                          </span>
                          {isSelected && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 ml-1" />
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-bold text-slate-600 font-mono">
                            {area.agent}
                          </span>
                          <span className="text-[9px] text-emerald-700 bg-emerald-100/80 px-1.5 py-0.2 rounded-xs font-mono font-bold shrink-0">
                            {formatPhoneDisplay(area.phone)}
                          </span>
                        </div>

                        {/* Badges: Client & Cost Center Scope */}
                        <div className="flex flex-wrap gap-1 pt-0.5">
                          {area.allClients || !area.clientIds || area.clientIds.length === 0 ? (
                            <span className="text-[8px] font-bold px-1.5 py-0.2 bg-blue-50 text-blue-700 rounded font-mono border border-blue-200/70">
                              🌐 Clientes General
                            </span>
                          ) : (
                            <span className="text-[8px] font-bold px-1.5 py-0.2 bg-blue-100 text-blue-800 rounded font-mono">
                              🏢 Asignado a {client?.code || 'Cliente'}
                            </span>
                          )}

                          {area.allCostCenters || !area.costCenterIds || area.costCenterIds.length === 0 ? (
                            <span className="text-[8px] font-bold px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded font-mono">
                              🌐 Todas las Sedes
                            </span>
                          ) : (
                            <span className="text-[8px] font-bold px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded font-mono">
                              📍 Sede Específica
                            </span>
                          )}
                        </div>

                        <p className="text-[9.5px] text-slate-500 leading-snug line-clamp-2 pt-0.5">
                          {area.desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* STEP 2: APPLICANT NAME CONFIRMATION (CONFIRMACIÓN DE SOLICITANTE) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-xl font-bold">
                  <User className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider font-mono text-slate-800">
                    PASO 2: Confirmación de Solicitante
                  </h3>
                  <p className="text-[10.5px] text-slate-500 font-medium">
                    Datos de quien notifica con roles correspondientes a <strong className="text-slate-800">{activeDept?.name || 'la Sede Activa'}</strong>
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-mono font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md border border-blue-100">
                Requerido
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              
              {/* Name Input */}
              <div className="sm:col-span-2 space-y-1">
                <label className="text-[10.5px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <span>Nombre del Solicitante / Persona que Notifica</span>
                  <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={solicitanteName}
                    onChange={(e) => setSolicitanteName(e.target.value)}
                    placeholder="Ej. Ing. Pedro Borja / Juan Pérez"
                    className="w-full bg-slate-50 hover:bg-slate-100/60 focus:bg-white text-xs pl-9 pr-3.5 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-semibold text-slate-900 transition-all"
                  />
                </div>
              </div>

              {/* Cargo / Rol Selector Chips (Filtrados por Departamento) */}
              <div className="sm:col-span-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                    Cargo / Rol del Solicitante (Correspondientes a {activeDept?.name || 'Soporte'})
                  </label>
                  <span className="text-[9px] text-blue-600 font-mono font-bold">
                    {availableRoles.length} opciones disponibles
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {availableRoles.map((roleOption) => {
                    const isSel = solicitanteRole === roleOption;
                    return (
                      <button
                        key={roleOption}
                        type="button"
                        onClick={() => setSolicitanteRole(roleOption)}
                        className={`px-2.5 py-1 rounded-lg text-[10.5px] font-bold transition-all cursor-pointer ${
                          isSel 
                            ? 'bg-slate-900 text-white shadow-2xs ring-2 ring-slate-900/30' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
                        }`}
                      >
                        {roleOption}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phone Optional Contact */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                  Teléfono / WhatsApp de Contacto (Opcional)
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={solicitantePhone}
                    onChange={(e) => setSolicitantePhone(e.target.value)}
                    placeholder="Ej. 8712345678"
                    className="w-full bg-slate-50 hover:bg-slate-100/60 focus:bg-white text-xs pl-9 pr-3.5 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 font-mono text-slate-900 transition-all"
                  />
                </div>
              </div>

              {/* Client Info Badge */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                  Empresa / Cliente Asociado
                </label>
                <div className="p-2 bg-slate-100/80 border border-slate-200 rounded-xl text-xs flex items-center justify-between font-mono">
                  <span className="font-bold text-slate-900 truncate">{client.name}</span>
                  <span className="text-[9px] font-black bg-white px-1.5 py-0.5 rounded border border-slate-300 text-slate-700 shrink-0">
                    {client.code || 'CLIENTE'}
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* STEP 3: INTERACTIVE TOPIC PRESETS (MOTIVO DE LA NOTIFICACIÓN FILTRADO POR DEPARTAMENTO) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl font-bold">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider font-mono text-slate-800 flex items-center gap-2">
                    <span>PASO 3: Motivo de la Notificación</span>
                    <span className="text-[10px] bg-indigo-100 text-indigo-800 font-bold px-2 py-0.5 rounded-full font-mono">
                      {activeDept?.name || 'General'}
                    </span>
                  </h3>
                  <p className="text-[10.5px] text-slate-500 font-medium">
                    Opciones exclusivas para <strong className="text-slate-800">{activeDept?.name || 'Atención'}</strong>
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-mono font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md border border-indigo-100">
                {availableTopics.length} Opciones
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-2.5">
              {availableTopics.map((topic) => {
                const Icon = topic.icon;
                const isSelected = selectedTopicKey === topic.key;

                return (
                  <button
                    key={topic.key}
                    type="button"
                    onClick={() => {
                      setSelectedTopicKey(topic.key);
                    }}
                    className={`text-left p-3 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-2 ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50/40 shadow-2xs font-bold ring-1 ring-indigo-500/30'
                        : 'border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className={`p-1.5 rounded-lg ${
                        isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-indigo-600"></span>
                      )}
                    </div>
                    <span className={`text-[11px] leading-tight ${
                      isSelected ? 'text-indigo-950 font-extrabold' : 'text-slate-800 font-medium'
                    }`}>
                      {topic.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* STEP 4: FOLIO ATTACHMENT (OPCIONAL) */}
            <div className="pt-2 border-t border-slate-100 space-y-2.5">
              <div className="flex items-center justify-between">
                <label className="text-[10.5px] font-black uppercase tracking-wider font-mono text-slate-700 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-slate-500" />
                  <span>Vincular Folio de Referencia (Opcional)</span>
                </label>
                {selectedFolioId && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFolioId('');
                      setSearchFolioQuery('');
                    }}
                    className="text-[10px] text-rose-600 hover:underline font-bold flex items-center gap-0.5 cursor-pointer"
                  >
                    <X className="h-3 w-3" /> Desvincular
                  </button>
                )}
              </div>

              {selectedFolioId && currentSelectedFolio ? (
                <div className="p-3 bg-emerald-50/60 border border-emerald-300 rounded-xl flex items-center justify-between gap-3 shadow-2xs">
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black text-slate-900 font-mono">
                        {currentSelectedFolio.id}
                      </span>
                      <span className={`text-[8.5px] font-black px-1.5 py-0.2 rounded uppercase tracking-wider border ${
                        currentSelectedFolio.status === 'ENTREGA_PLANTA' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                        currentSelectedFolio.status === 'RETRABAJO' ? 'bg-rose-100 text-rose-800 border-rose-300' :
                        'bg-slate-200 text-slate-800 border-slate-300'
                      }`}>
                        {currentSelectedFolio.status}
                      </span>
                    </div>
                    <p className="text-[10.5px] font-bold text-slate-700 truncate">
                      {currentSelectedFolio.costCenterName}
                    </p>
                    <p className="text-[9.5px] text-slate-500 font-mono">
                      {currentSelectedFolio.items?.length || 0} ítems • {currentSelectedFolio.hasGranallado ? 'Granallado Incluido' : 'Sin Granallado'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedFolioId('')}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white rounded-lg transition-colors shrink-0 cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={searchFolioQuery}
                      onChange={(e) => setSearchFolioQuery(e.target.value)}
                      placeholder="Buscar folio activo por número o planta (Ej. F-102501)..."
                      className="w-full bg-slate-50 hover:bg-slate-100/50 focus:bg-white text-xs pl-9 pr-3.5 py-2 border border-slate-200 rounded-xl outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 font-mono text-slate-900 transition-all"
                    />
                  </div>

                  {searchFolioQuery && (
                    <div className="border border-slate-200 rounded-xl max-h-40 overflow-y-auto bg-white divide-y divide-slate-100 shadow-sm">
                      {filteredFoliosList.length === 0 ? (
                        <div className="p-3 text-center text-[10.5px] text-slate-400">
                          No se encontraron folios coincidentes
                        </div>
                      ) : (
                        filteredFoliosList.slice(0, 6).map((folio) => (
                          <button
                            key={folio.id}
                            type="button"
                            onClick={() => {
                              setSelectedFolioId(folio.id);
                              setSearchFolioQuery('');
                            }}
                            className="w-full text-left p-2.5 hover:bg-slate-50 flex items-center justify-between gap-3 text-xs cursor-pointer transition-colors"
                          >
                            <div>
                              <span className="font-bold font-mono text-slate-900">{folio.id}</span>
                              <span className="text-[10px] text-slate-500 block">{folio.costCenterName}</span>
                            </div>
                            <span className="text-[9px] font-mono font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200">
                              {folio.status}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CUSTOM OBSERVATIONS / ADDITIONAL DETAILS */}
            <div className="space-y-1.5 pt-2 border-t border-slate-100">
              <label className="text-[10.5px] font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                <span>Detalles o Comentarios Adicionales</span>
                <span className="text-[9.5px] text-slate-400 font-normal font-mono">Opcional</span>
              </label>
              <textarea
                value={customDetailText}
                onChange={(e) => setCustomDetailText(e.target.value)}
                rows={3}
                placeholder="Escribe aclaraciones específicas, número de guía, horario preferido o notas especiales..."
                className="w-full bg-slate-50 hover:bg-slate-100/50 focus:bg-white text-xs p-3 border border-slate-200 rounded-xl outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-slate-900 transition-all resize-y"
              />
            </div>

          </div>

        </div>

        {/* RIGHT COLUMN: Real-time Formatted WhatsApp Message Preview & Action Card */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* WHATSAPP MESSAGE PREVIEW CONTAINER */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-lg overflow-hidden flex flex-col">
            
            {/* Header simulating WhatsApp Chat Header */}
            <div className="bg-slate-855 p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-black shadow-inner">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <span className="absolute bottom-0 right-0 h-3 w-3 bg-emerald-400 rounded-full border-2 border-slate-900"></span>
                </div>
                
                <div>
                  <h4 className="text-xs font-black text-white font-display tracking-wide truncate">
                    {activeDept ? activeDept.name : 'Sin línea seleccionada'}
                  </h4>
                  <p className="text-[10px] text-emerald-400 font-bold font-mono truncate">
                    {activeDept ? `${activeDept.agent} • ${formatPhoneDisplay(activeDept.phone)}` : (costCenter ? `Sede: ${costCenter.name}` : 'No disponible')}
                  </p>
                </div>
              </div>

              <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-black uppercase tracking-wider rounded-md font-mono">
                {activeDept ? 'Línea Directa' : 'Inactivo'}
              </span>
            </div>

            {/* Simulated WhatsApp Bubble Area */}
            <div className="p-4 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px] bg-slate-950 flex-1 min-h-[360px] max-h-[500px] overflow-y-auto space-y-3">
              
              <div className="text-center my-1">
                <span className="bg-slate-800 text-slate-400 text-[9px] px-2.5 py-1 rounded-full font-mono">
                  MENSAJE PRE-FORMATO PARA WHATSAPP
                </span>
              </div>

              {!activeDept ? (
                <div className="bg-amber-950/30 border border-amber-800/40 p-5 rounded-2xl text-amber-200 text-center space-y-2 my-6">
                  <ShieldAlert className="h-7 w-7 mx-auto text-amber-400" />
                  <h5 className="text-xs font-black uppercase tracking-wider font-mono">
                    Sin atención directa configurada
                  </h5>
                  <p className="text-[11px] text-amber-300/80 max-w-xs mx-auto leading-relaxed">
                    No existe una línea de WhatsApp asignada a <strong className="text-white">{costCenter?.name || 'este Centro de Costos'}</strong>.
                  </p>
                </div>
              ) : (
                /* Chat Bubble Card */
                <div className="bg-emerald-950/40 border border-emerald-800/40 p-4 rounded-2xl rounded-tr-none text-slate-100 shadow-md space-y-2 ml-auto max-w-full font-mono text-[11px] leading-relaxed relative">
                  <div className="whitespace-pre-wrap break-words text-slate-200">
                    {formattedWhatsAppMessage}
                  </div>
                  
                  <div className="flex items-center justify-end gap-1 text-[9px] text-emerald-400/80 pt-1 border-t border-emerald-900/50">
                    <span>Ahora</span>
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  </div>
                </div>
              )}

            </div>

            {/* ACTION BUTTONS PANEL */}
            <div className="p-4 bg-slate-900 border-t border-slate-800 space-y-2.5">
              
              {/* PRIMARY BUTTON: OPEN WHATSAPP DIRECT */}
              <button
                type="button"
                onClick={handleOpenWhatsApp}
                disabled={!activeDept || !activeDept.phone}
                className={`w-full font-black py-3 px-4 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all shadow-lg ${
                  activeDept && activeDept.phone
                    ? 'bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] text-slate-950 shadow-emerald-500/20 cursor-pointer'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed shadow-none border border-slate-700'
                }`}
              >
                <MessageCircle className="h-5 w-5 shrink-0" />
                <span>{activeDept ? 'Enviar Notificación por WhatsApp' : 'Línea no disponible para esta sede'}</span>
                {activeDept && <ExternalLink className="h-4 w-4 shrink-0 ml-auto" />}
              </button>

              {/* SECONDARY BUTTONS */}
              <div className="grid grid-cols-2 gap-2">
                
                {/* Copy Text Button */}
                <button
                  type="button"
                  onClick={handleCopyMessage}
                  className={`py-2 px-3 rounded-xl border text-[10.5px] font-extrabold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    copiedSuccess
                      ? 'bg-emerald-900/40 border-emerald-500 text-emerald-300'
                      : 'bg-slate-800 hover:bg-slate-750 border-slate-700 text-slate-200'
                  }`}
                >
                  {copiedSuccess ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-400" />
                      <span>¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 text-slate-400" />
                      <span>Copiar Texto</span>
                    </>
                  )}
                </button>

                {/* QR Code Trigger Button */}
                <button
                  type="button"
                  onClick={() => setShowQrModal(true)}
                  className="py-2 px-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 text-slate-200 rounded-xl text-[10.5px] font-extrabold flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                >
                  <QrCode className="h-3.5 w-3.5 text-slate-400" />
                  <span>Código QR Móvil</span>
                </button>

              </div>

            </div>

          </div>

          {/* LOCAL DISPATCH LOGS (HISTORIAL DE NOTIFICACIONES ENVIADAS) */}
          {dispatchLogs.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-xs space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-slate-500" />
                  <h4 className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                    Envíos Recientes ({dispatchLogs.length})
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => setDispatchLogs([])}
                  className="text-[10px] text-slate-400 hover:text-rose-600 font-bold"
                >
                  Limpiar todo
                </button>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pr-1 divide-y divide-slate-100">
                {dispatchLogs.map((log) => (
                  <div key={log.id} className="pt-2 first:pt-0 flex items-start justify-between gap-2 text-[11px]">
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-slate-900 truncate">{log.departmentName}</span>
                        {log.folioId && (
                          <span className="text-[9px] font-mono bg-slate-100 text-slate-700 px-1 py-0.2 rounded">
                            {log.folioId}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500 font-medium">
                        Solicitó: <strong>{log.solicitanteName}</strong> • {log.topicTitle}
                      </p>
                      <p className="text-[9px] text-slate-400 font-mono">
                        {new Date(log.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleClearLog(log.id)}
                      className="text-slate-300 hover:text-rose-500 p-1 shrink-0"
                      title="Eliminar de historial local"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

      </div>

      {/* QR CODE MODAL FOR MOBILE SCANNING */}
      {showQrModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-2xl relative border border-slate-100">
            <button
              type="button"
              onClick={() => setShowQrModal(false)}
              className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-center space-y-1">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl w-fit mx-auto mb-2">
                <QrCode className="h-8 w-8" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900 font-display">
                Escanear con Teléfono Móvil
              </h3>
              <p className="text-xs text-slate-500">
                Abre la cámara de tu smartphone para iniciar la conversación de WhatsApp con **{activeDept.agent}** directamente en tu celular.
              </p>
            </div>

            {/* QR Image via public API */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col items-center justify-center">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(whatsappUrl)}`}
                alt="Código QR WhatsApp"
                className="w-48 h-48 rounded-lg shadow-xs"
              />
              <span className="text-[10px] text-slate-400 font-mono mt-2">
                Línea Directa: {formatPhoneDisplay(activeDept.phone)}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setShowQrModal(false)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl text-xs uppercase tracking-wider cursor-pointer"
            >
              Cerrar Ventana
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
